import apiClient from './api';

/**
 * WordPress Channel API Service
 *
 * Typed client for `/api/wordpress/*` — connecting self-hosted WordPress
 * sites (Application Passwords, v1 scope per SPEC.md), listing/reprobing/
 * disconnecting them, and fetching categories/tags for the blog preview
 * screen's pickers (decision F).
 *
 * All responses are the "safe channel" shape from the backend — secrets
 * (accessToken, encryptedAppPassword) are never present.
 */

/**
 * Health-probing a remote (customer-hosted) WordPress site can be slow —
 * beyond the shared axios client's 10s default (see `api.ts`) — so connect
 * and reprobe get a longer allowance instead of surfacing a misleading
 * "Unable to connect to server" network error on a merely-slow site.
 */
const WP_PROBE_TIMEOUT = 60_000;

export interface WordPressChannel {
  id: number;
  teamId: number;
  platform: 'WORDPRESS';
  siteUrl: string;
  wpUsername: string;
  /** Public http(s) URL of the site's WordPress "Site Icon" (favicon), when set. */
  siteIconUrl?: string;
  capabilities: string[];
  isActive: boolean;
  lastHealthCheckAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WordPressCategory {
  id: number;
  name: string;
  slug: string;
  count: number;
}

export interface WordPressTag {
  id: number;
  name: string;
  slug: string;
  count: number;
}

export interface ConnectWordPressInput {
  siteUrl: string;
  username: string;
  applicationPassword: string;
}

export interface ReprobeWordPressResult {
  channel: WordPressChannel;
  health: {
    status: string;
    message: string;
  };
}

/** POST create-term response shape — just the new/existing term's id + name
 *  (not the full `WordPressCategory`/`WordPressTag` shape, which additionally
 *  has `slug`/`count` only returned by the GET list endpoints). */
export interface WordPressTermResult {
  id: number;
  name: string;
}

export const wordpressApi = {
  /**
   * POST /api/wordpress/connect
   * Probes the site's health before persisting. On a health-probe failure
   * the backend returns a 4xx with `{ success: false, code, message }` —
   * callers should surface `message` inline (CLAUDE.md rule 15), not a
   * generic toast.
   */
  connect: async (input: ConnectWordPressInput): Promise<WordPressChannel> => {
    const response = await apiClient.post<{ success: true; data: WordPressChannel }>(
      '/api/wordpress/connect',
      input,
      { timeout: WP_PROBE_TIMEOUT }
    );
    return response.data.data;
  },

  /**
   * GET /api/wordpress/channels
   */
  getChannels: async (): Promise<WordPressChannel[]> => {
    const response = await apiClient.get<{ success: true; data: WordPressChannel[] }>(
      '/api/wordpress/channels'
    );
    return response.data.data;
  },

  /**
   * POST /api/wordpress/channels/:id/reprobe
   * Re-runs the health probe — never trust a stale "connected" state
   * (CLAUDE.md rule 15).
   */
  reprobe: async (channelId: number): Promise<ReprobeWordPressResult> => {
    const response = await apiClient.post<{ success: true; data: ReprobeWordPressResult }>(
      `/api/wordpress/channels/${channelId}/reprobe`,
      undefined,
      { timeout: WP_PROBE_TIMEOUT }
    );
    return response.data.data;
  },

  /**
   * DELETE /api/wordpress/channels/:id
   * Soft-disconnect — existing blog content survives (BlogPost.channelId is
   * onDelete: SetNull).
   */
  disconnect: async (channelId: number): Promise<void> => {
    await apiClient.delete(`/api/wordpress/channels/${channelId}`);
  },

  /**
   * GET /api/wordpress/channels/:id/categories
   */
  getCategories: async (channelId: number): Promise<WordPressCategory[]> => {
    const response = await apiClient.get<{ success: true; data: WordPressCategory[] }>(
      `/api/wordpress/channels/${channelId}/categories`
    );
    return response.data.data;
  },

  /**
   * GET /api/wordpress/channels/:id/tags
   */
  getTags: async (channelId: number): Promise<WordPressTag[]> => {
    const response = await apiClient.get<{ success: true; data: WordPressTag[] }>(
      `/api/wordpress/channels/${channelId}/tags`
    );
    return response.data.data;
  },

  /**
   * POST /api/wordpress/channels/:id/categories
   * Creates a new category on the connected WP site. Returns the EXISTING
   * term instead of erroring when WordPress reports `term_exists`.
   */
  createCategory: async (channelId: number, name: string): Promise<WordPressTermResult> => {
    const response = await apiClient.post<{ success: true; data: WordPressTermResult }>(
      `/api/wordpress/channels/${channelId}/categories`,
      { name }
    );
    return response.data.data;
  },

  /**
   * POST /api/wordpress/channels/:id/tags
   * Creates a new tag on the connected WP site. Returns the EXISTING term
   * instead of erroring when WordPress reports `term_exists`.
   */
  createTag: async (channelId: number, name: string): Promise<WordPressTermResult> => {
    const response = await apiClient.post<{ success: true; data: WordPressTermResult }>(
      `/api/wordpress/channels/${channelId}/tags`,
      { name }
    );
    return response.data.data;
  },
};

export default wordpressApi;
